import { FC, MouseEvent } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price';
import { Link } from 'dibs-elements/exports/Link';
import * as tracking from 'dibs-tracking';

import styles from './PdpSharedDetail.scss';
import {
    useItemTranslations,
    UseItemTranslationState,
} from '../../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

import { PdpSharedDetail_detailValues$key } from './__generated__/PdpSharedDetail_detailValues.graphql';

type OnClickMap = {
    [key: string]: {
        clickFn: () => void;
        dataTn: string;
    };
};

export type PdpSharedDetailProps = {
    detailValues: PdpSharedDetail_detailValues$key | null;
    currency: string;
    delimiter?: string | null;
    displayValuesAs?: 'block' | 'inline' | 'inline-block';
    // only optional b/c the `PdpSharedDetailCondition` component doesn't require this for some reason
    // otherwise every other pdp detail component does
    selectedSkuId?: string | null;
    onClickMap?: OnClickMap;
    limitLength?: boolean;
};

function getDetailDispalyValue({
    state,
    detailKey,
    displayDetailKey,
}: {
    state: UseItemTranslationState[0];
    detailKey: string;
    displayDetailKey: string;
}): string | undefined | null {
    return state?.data?.viewer.item?.pdpDetailsDisplay
        ?.find(details => details?.detailKey === displayDetailKey)
        ?.detailValues?.find(detail => detail?.detailKey === detailKey)?.displayName;
}

export const PdpSharedDetail: FC<PdpSharedDetailProps> = ({
    detailValues: detailValuesRef,
    delimiter,
    displayValuesAs,
    currency,
    selectedSkuId,
    onClickMap,
    limitLength,
}) => {
    const detailValues =
        useFragment(
            graphql`
                fragment PdpSharedDetail_detailValues on PdpDetailValueInterface
                @relay(plural: true) {
                    detailKey
                    browseUrl
                    displayName
                    displayDetails
                    skuId
                    ... on PdpDetailVersionsValue {
                        price {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                }
            `,
            detailValuesRef
        ) || [];
    const [translationState] = useItemTranslations();

    return (
        <>
            {detailValues.map((detailValue, index) => {
                if (selectedSkuId && detailValue.skuId && detailValue.skuId !== selectedSkuId) {
                    return null;
                }
                let { displayName } = detailValue;
                const { browseUrl, detailKey, displayDetails } = detailValue;
                if (translationState?.interfaceState === 'SHOW_ORIGINAL') {
                    if (detailKey === 'conditionDetails') {
                        displayName =
                            getDetailDispalyValue({
                                state: translationState,
                                detailKey: detailKey,
                                displayDetailKey: 'CONDITION',
                            }) || displayName;
                    }
                    if (detailKey === 'dateOfManufacture') {
                        displayName =
                            getDetailDispalyValue({
                                state: translationState,
                                detailKey: detailKey,
                                displayDetailKey: 'DATE_OF_MANUFACTURE',
                            }) || displayName;
                    }
                }
                const key = `${detailKey}-${index}`;
                const nextIndex = index + 1;

                const isLastDetail = !detailValues?.[nextIndex];
                const addDelimiter = !isLastDetail && delimiter;

                if (!displayValuesAs) {
                    if (!delimiter && detailValues.length > 1) {
                        displayValuesAs = 'block';
                    } else if (addDelimiter) {
                        displayValuesAs = 'inline';
                    }
                }

                const detailClass = classnames({
                    [styles.detailBlock]: displayValuesAs === 'block',
                    [styles.detailInline]: displayValuesAs === 'inline',
                    [styles.detailInlineBlock]: displayValuesAs === 'inline-block',
                    [styles.inlineSpace]: !isLastDetail && displayValuesAs !== 'block',
                    [styles.limitLength]: limitLength,
                });

                let detailEl;
                const convertedAmts = detailValue?.price?.convertedAmountList;
                let priceEl;
                const convertedAmt =
                    convertedAmts && convertedAmts.find(amt => amt?.currency === currency);

                const handleClick = (event: MouseEvent<HTMLElement | Element>): void => {
                    if (detailKey) {
                        const action = `view ${detailKey} click`;
                        tracking.trackEvent(
                            {
                                category: 'product',
                                action,
                                label: 'pdp attribute',
                                eventName: 'product_interaction',
                                step_interaction_name: action as tracking.StepInteractionNameValues,
                                interaction_type:
                                    tracking.interactionTypeConstants
                                        .INTERACTION_TYPE_PDP_ATTRIBUTE,
                            },
                            event
                        );
                    }
                };
                if (convertedAmt && convertedAmt.amount) {
                    priceEl = (
                        <span className={styles.secondaryLink}>
                            <FormattedMessage
                                id="abf.PdpSharedDetail.price"
                                defaultMessage="Price:"
                            />{' '}
                            <FormattedPrice
                                currency={currency}
                                value={Math.round(convertedAmt.amount)}
                            />
                        </span>
                    );
                }
                if (browseUrl && detailKey && onClickMap?.[detailKey]) {
                    detailEl = (
                        <Link
                            onClick={onClickMap[detailKey].clickFn}
                            dataTn={onClickMap[detailKey].dataTn}
                            className={styles.details}
                        >
                            {displayName}
                        </Link>
                    );
                } else if (browseUrl) {
                    detailEl = (
                        <Link href={browseUrl} className={styles.details} onClick={handleClick}>
                            {displayName}
                        </Link>
                    );
                } else if (detailKey && onClickMap?.[detailKey]) {
                    detailEl = (
                        <Link
                            onClick={event => {
                                handleClick(event);
                                onClickMap[detailKey].clickFn();
                            }}
                            dataTn={onClickMap[detailKey].dataTn}
                            className={styles.details}
                        >
                            {displayName}
                            {priceEl}
                        </Link>
                    );
                } else {
                    detailEl = (
                        <span className={styles.details}>
                            {displayName}
                            {priceEl}
                        </span>
                    );
                }
                return (
                    <span
                        key={key}
                        className={detailClass}
                        data-tn={`pdp-spec-detail-${detailKey}`}
                    >
                        {detailEl}
                        {displayDetails && <> {displayDetails}</>}
                        {addDelimiter && delimiter}
                    </span>
                );
            })}
        </>
    );
};
